/**
 * Web short-drama SDK bridge.
 *
 * The drama UI may run as a normal Starparks page or inside an iframe owned by
 * another app.  In the latter case the host initializes it with postMessage
 * and is responsible for payment and rewarded-ad presentation.  No secret is
 * accepted through query parameters.
 */
import { ref } from "vue";

export interface DramaSdkConfig {
  userId: string;
  language?: string;
  /** Opaque, short-lived credential minted by the drama backend. */
  accessToken?: string;
  /** Optional allow-list item used to validate the embedding host origin. */
  hostOrigin?: string;
}

export interface DramaPaymentRequest {
  orderId?: string;
  goodsId?: string | number;
  goodsType?: number;
  dramasId: number;
  dramasVideoId?: number;
  productId?: string;
}

export interface DramaAdRequest {
  adType: "rewarded" | "interstitial";
  dramasId: number;
  dramasVideoId?: number;
}

type HostMessage = {
  channel: "starparks-drama-sdk";
  type: string;
  requestId?: string;
  payload?: Record<string, unknown>;
};

export const dramaSdkConfig = ref<DramaSdkConfig | null>(null);
export const isDramaEmbedded = ref(false);

const pending = new Map<string, (payload: Record<string, unknown>) => void>();
let initialized = false;
const configuredOrigins = (import.meta.env.VITE_DRAMA_ALLOWED_HOST_ORIGINS ?? "")
  .split(",")
  .map((origin: string) => origin.trim())
  .filter(Boolean);

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

function newRequestId(): string {
  return `drama_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function isAllowedOrigin(origin: string): boolean {
  const expected = dramaSdkConfig.value?.hostOrigin;
  if (expected) return expected === origin;
  return configuredOrigins.length === 0 || configuredOrigins.includes(origin);
}

function emit(type: string, payload: Record<string, unknown>, requestId?: string) {
  const message: HostMessage = { channel: "starparks-drama-sdk", type, payload, requestId };
  // Native wrappers can listen without having to host an iframe.
  window.dispatchEvent(new CustomEvent("starparks:drama-sdk", { detail: message }));
  if (window.parent !== window) {
    window.parent.postMessage(message, dramaSdkConfig.value?.hostOrigin ?? "*");
  }
}

/** Start listening once. It is safe to call during normal, non-embedded use. */
export function initializeDramaSdk(): void {
  if (initialized || typeof window === "undefined") return;
  initialized = true;
  isDramaEmbedded.value = window.parent !== window;
  window.addEventListener("message", (event: MessageEvent<unknown>) => {
    if (!isRecord(event.data)) return;
    const message = event.data as HostMessage;
    if (message.channel !== "starparks-drama-sdk" || !isAllowedOrigin(event.origin)) return;

    if (message.type === "initialize") {
      const payload = message.payload ?? {};
      const userId = String(payload.userId ?? "").trim();
      if (!userId) {
        emit("initialize-error", { code: "USER_ID_REQUIRED" }, message.requestId);
        return;
      }
      dramaSdkConfig.value = {
        userId,
        language: typeof payload.language === "string" ? payload.language : undefined,
        accessToken: typeof payload.accessToken === "string" ? payload.accessToken : undefined,
        hostOrigin: event.origin,
      };
      isDramaEmbedded.value = true;
      emit("initialized", { userId }, message.requestId);
      return;
    }

    if (message.type === "refresh-entitlements") {
      window.dispatchEvent(new CustomEvent("starparks:drama-entitlements-changed"));
      return;
    }

    if (message.requestId && pending.has(message.requestId)) {
      pending.get(message.requestId)?.(message.payload ?? {});
      pending.delete(message.requestId);
    }
  });
  emit("ready", { version: "1.0" });
}

async function requestHost(
  type: "request-payment" | "request-rewarded-ad",
  payload: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  const requestId = newRequestId();
  const response = new Promise<Record<string, unknown>>((resolve) => {
    pending.set(requestId, resolve);
    window.setTimeout(() => {
      if (pending.delete(requestId)) resolve({ accepted: false, timeout: true });
    }, 60_000);
  });
  emit(type, payload, requestId);
  return response;
}

export async function requestDramaPayment(request: DramaPaymentRequest): Promise<boolean> {
  const result = await requestHost("request-payment", request as unknown as Record<string, unknown>);
  return result.accepted === true || result.success === true;
}

export async function requestDramaRewardedAd(request: DramaAdRequest): Promise<boolean> {
  const result = await requestHost("request-rewarded-ad", request as unknown as Record<string, unknown>);
  return result.rewarded === true || result.success === true;
}
